/**
 * A logo chosen in Admin, made small before it is stored. The logo travels with the branding in every activation, every new-game
 * check, every shared game and the TV screen's data, and it is kept on every phone — a 250 KB picture there made first activation
 * and Admin crawl on a weak mobile connection (2026-10-06). So it is scaled to at most `LOGO_MAX_WIDTH` pixels wide (a header logo is
 * about 3.6 rem tall) and re-encoded as PNG, which keeps any transparency; if that is still large, as WebP.
 */

export const LOGO_MAX_WIDTH = 420;
/** Above this many characters of data URL the PNG is tried again as WebP. */
export const LOGO_PREFERRED_MAX_CHARS = 60_000;

/** The size to scale a picture to: never up, never wider than the limit, the shape kept. */
export function logoTargetSize(width: number, height: number, maxWidth = LOGO_MAX_WIDTH): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width: 1, height: 1 };
  if (width <= maxWidth) return { width, height };
  return { width: maxWidth, height: Math.max(1, Math.round((height * maxWidth) / width)) };
}

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Could not read the image.'));
    reader.readAsDataURL(file);
  });
}

/** The chosen picture as a small data URL. If the browser cannot decode it, the file is used as it is (as before). */
export async function readLogoFile(file: File): Promise<string> {
  const original = await readAsDataUrl(file);
  try {
    const image = new Image();
    image.src = original;
    await image.decode();
    const { width, height } = logoTargetSize(image.naturalWidth, image.naturalHeight);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return original;
    context.drawImage(image, 0, 0, width, height);
    let out = canvas.toDataURL('image/png');
    if (out.length > LOGO_PREFERRED_MAX_CHARS) {
      const webp = canvas.toDataURL('image/webp', 0.85);
      if (webp.startsWith('data:image/webp') && webp.length < out.length) out = webp;
    }
    return out.length < original.length ? out : original;
  } catch {
    return original;
  }
}
